import React, { useCallback, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import {
	DataGrid,
	GridCellModes,
	GridCellModesModel,
	GridCellParams,
	GridColDef,
	GridEditSingleSelectCell,
	GridRenderCellParams,
	GridRenderEditCellParams,
	useGridApiContext,
} from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { IAdminTask, IAdminTaskAssignee, TASKS_QUERY_KEY, toTaskUpdate, updateTask } from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';
import { formatDate, toIsoDate } from '../../utils/dates';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';
import TaskAssigneeLabel from './TaskAssigneeLabel';
import TaskDialog from './TaskDialog';
import TaskStatusChip from './TaskStatusChip';
import TaskSubjectLinks from './TaskSubjectLinks';
import TaskTypeChip from './TaskTypeChip';
import { TASK_STATUS_OPTIONS, TASK_TYPE_OPTIONS, taskTypeLabel, userLabel } from './taskOptions';

interface Props {
	tasks: IAdminTask[];
	loading: boolean;
	/** Who can be picked as the assignee: admins holding the tasks permission. */
	assignees: IAdminTaskAssignee[];
	/** Briefly highlight this row (the task just added by the quick-add line). */
	highlightId?: number | null;
	height?: string | number;
}

/** Larger text, chips and avatars than the dashboard default, so a task list reads comfortably. */
export const TASK_GRID_SX = {
	'& .MuiDataGrid-cell, & .MuiDataGrid-columnHeaderTitle': { fontSize: '0.95rem' },
	'& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
	'& .MuiDataGrid-cell .MuiTypography-body2, & .MuiDataGrid-cell .MuiInputBase-root': { fontSize: 'inherit' },
	'& .MuiDataGrid-cell .MuiTypography-caption': { fontSize: '0.75rem' },
	'& .MuiDataGrid-cell .MuiChip-root': { fontSize: '0.875rem', height: 28 },
	'& .MuiDataGrid-cell .MuiAvatar-root': { width: 28, height: 28, fontSize: 12 },
} as const;

/** The grid's date editor works in Date objects; the API works in local YYYY-MM-DD. */
const fromIsoDate = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`) : null);

/** Select editor that saves as soon as an option is picked, instead of waiting for Enter or a click elsewhere. */
function AutoCommitSelectCell(params: GridRenderEditCellParams) {
	const apiRef = useGridApiContext();
	return (
		<GridEditSingleSelectCell
			{...params}
			onValueChange={() => {
				// The grid stores the picked value right after this callback; commit on the next tick.
				setTimeout(() => apiRef.current.stopCellEditMode({ id: params.id, field: params.field }), 0);
			}}
		/>
	);
}

const renderSelectEditCell = (params: GridRenderEditCellParams) => <AutoCommitSelectCell {...params} />;

/**
 * Every task, one row each. Status, title, type, assignee and due date are edited in place
 * with a single click; subjects, notes and delete go through the pencil (the full form).
 */
export default function TasksTable({ tasks, loading, assignees, highlightId = null, height = 'calc(100vh - 330px)' }: Props) {
	const queryClient = useQueryClient();
	const [editing, setEditing] = useState<IAdminTask | null>(null);
	const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
	const [error, setError] = useState<string | null>(null);

	// Single click starts editing (the grid's default is a double click).
	const handleCellClick = useCallback((params: GridCellParams, event: React.MouseEvent) => {
		if (!params.isEditable) return;
		if (!event.currentTarget.contains(event.target as Element)) return; // click inside a portal (open select)
		setCellModesModel((prev) => ({
			...Object.keys(prev).reduce(
				(acc, id) => ({
					...acc,
					[id]: Object.keys(prev[id]).reduce((row, field) => ({ ...row, [field]: { mode: GridCellModes.View } }), {}),
				}),
				{},
			),
			[params.id]: {
				...Object.keys(prev[params.id] ?? {}).reduce((row, field) => ({ ...row, [field]: { mode: GridCellModes.View } }), {}),
				[params.field]: { mode: GridCellModes.Edit },
			},
		}));
	}, []);

	const processRowUpdate = useCallback(
		async (newRow: IAdminTask, oldRow: IAdminTask) => {
			const payload = toTaskUpdate(newRow);
			if (JSON.stringify(payload) === JSON.stringify(toTaskUpdate(oldRow))) return oldRow;
			const saved = await updateTask(newRow.id, payload);
			queryClient.invalidateQueries(TASKS_QUERY_KEY); // the grid shows `saved` right away; this refreshes every list
			setError(null);
			return saved;
		},
		[queryClient],
	);

	// Memoised: a new columns array makes the grid drop its column state (widths) on every cell click.
	const columns = useMemo<GridColDef[]>(() => [
		{
			field: 'status',
			headerName: 'Status',
			width: 125,
			type: 'singleSelect',
			valueOptions: TASK_STATUS_OPTIONS,
			editable: true,
			renderCell: (p: GridRenderCellParams) => <TaskStatusChip status={p.value} />,
			renderEditCell: renderSelectEditCell,
		},
		{ field: 'title', headerName: 'Title', flex: 2, minWidth: 240, editable: true },
		{
			field: 'type',
			headerName: 'Type',
			width: 135,
			type: 'singleSelect',
			valueOptions: TASK_TYPE_OPTIONS,
			editable: true,
			valueFormatter: ({ value }) => taskTypeLabel(value),
			renderCell: (p: GridRenderCellParams) => <TaskTypeChip type={p.value} />,
			renderEditCell: renderSelectEditCell,
		},
		{
			field: 'subjects',
			headerName: 'Tagged to',
			flex: 1.4,
			minWidth: 180,
			sortable: false,
			valueGetter: ({ row }) => (row as IAdminTask).subjects.map((s) => s.name).join(', '),
			renderCell: (p: GridRenderCellParams) => <TaskSubjectLinks task={p.row as IAdminTask} />,
		},
		{
			field: 'assignee_user_id',
			headerName: 'Assignee',
			width: 160,
			type: 'singleSelect',
			// 0 stands in for "unassigned": the editor needs a real option to select
			valueOptions: [{ value: 0, label: 'Unassigned' }, ...assignees.map((u) => ({ value: u.id, label: userLabel(u) }))],
			editable: true,
			valueGetter: ({ row }) => (row as IAdminTask).assignee_user_id ?? 0,
			valueSetter: ({ value, row }) => ({ ...row, assignee_user_id: value ? Number(value) : null }),
			valueFormatter: ({ value }) => {
				const task = assignees.find((u) => u.id === value);
				return value ? (task ? userLabel(task) : '') : '';
			},
			renderCell: (p: GridRenderCellParams) => {
				const task = p.row as IAdminTask;
				if (task.assignee_user_id === null) {
					return null;
				}
				return (
					<TaskAssigneeLabel
						user={{ id: task.assignee_user_id, full_name: task.assignee_name, email: task.assignee_email ?? '' }}
						assignees={assignees}
					/>
				);
			},
			renderEditCell: renderSelectEditCell,
		},
		{
			field: 'due_date',
			headerName: 'Due',
			width: 115,
			type: 'date',
			editable: true,
			valueGetter: ({ row }) => fromIsoDate((row as IAdminTask).due_date),
			valueSetter: ({ value, row }) => ({ ...row, due_date: value ? toIsoDate(value as Date) : null }),
			valueFormatter: ({ value }) => (value ? toIsoDate(value as Date) : ''),
		},
		{
			field: 'completed_date',
			headerName: 'Completed',
			width: 110,
			valueFormatter: ({ value }) => formatDate(value),
		},
		{
			field: 'updated_at',
			headerName: 'Updated',
			width: 105,
			valueFormatter: ({ value }) => formatDate(value),
		},
		{
			field: 'actions',
			headerName: '',
			width: 48,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (p: GridRenderCellParams) => (
				<Tooltip title="Edit details (tags, notes, delete)">
					<IconButton size="small" onClick={() => setEditing(p.row as IAdminTask)}>
						<EditIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
	], [assignees]);

	return (
		<Box
			sx={{
				height,
				width: '100%',
				'@keyframes taskRowFlash': { from: { backgroundColor: 'rgba(144, 202, 249, 0.22)' }, to: { backgroundColor: 'transparent' } },
				'& .task-row-new': { animation: 'taskRowFlash 1.6s ease-out' },
				'& .MuiDataGrid-cell--editable': { cursor: 'pointer' },
				...TASK_GRID_SX,
			}}
		>
			{error && (
				<Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}
			<DataGrid
				rows={tasks}
				columns={columns}
				loading={loading}
				density="comfortable"
				disableSelectionOnClick
				experimentalFeatures={{ newEditingApi: true }}
				editMode="cell"
				cellModesModel={cellModesModel}
				onCellModesModelChange={(model) => setCellModesModel(model)}
				onCellClick={handleCellClick}
				processRowUpdate={processRowUpdate}
				onProcessRowUpdateError={(e) => setError(apiErrorMessage(e, 'Could not save the change. Please try again.'))}
				getRowClassName={(p) => (p.id === highlightId ? 'task-row-new' : '')}
				components={{ Toolbar: AdminQuickFilterToolbar }}
				componentsProps={{ toolbar: { showQuickFilter: true } }}
			/>
			<TaskDialog open={!!editing} onClose={() => setEditing(null)} task={editing} />
		</Box>
	);
}
